import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { getAdminCatalogCards } from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { ArchiveCatalogRecordButton } from './-components/archive-catalog-record-button'
import { CreateCardDialog } from './-components/create-card-dialog'
import { DebouncedSearchInput } from './-components/debounced-search-input'
import { EditCardDialog } from './-components/edit-card-dialog'
import { FilterSelect } from './-components/filter-select'
import { ResetFiltersButton } from './-components/reset-filters-button'

const CARD_CATEGORIES = [
	'leader',
	'character',
	'event',
	'stage',
	'don'
] as const

export const Route = createFileRoute('/cards')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({
		category: z.enum(CARD_CATEGORIES).optional(),
		direction: z.enum(['asc', 'desc']).optional(),
		limit: z.coerce.number().int().min(10).max(100).optional(),
		offset: z.coerce.number().int().nonnegative().optional(),
		q: z.string().optional(),
		sortBy: z
			.enum(['block', 'cardNumber', 'category', 'cost', 'power'])
			.optional()
	}),
	component: CardsPage
})

function CardsPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const limit = search.limit ?? 50
	const offset = search.offset ?? 0
	const sortBy = search.sortBy ?? 'cardNumber'
	const direction = search.direction ?? 'asc'
	const cardsQuery = useQuery({
		queryKey: ['catalog', 'cards', search],
		queryFn: () =>
			getAdminCatalogCards({
				query: {
					category: search.category,
					direction,
					limit,
					offset,
					q: search.q,
					sortBy
				}
			}).then((response) => response.data)
	})
	const data = cardsQuery.data?.data ?? []
	const hasActiveFilters = Boolean(search.category || search.q)
	const columns: DataTableColumn<(typeof data)[number], typeof sortBy>[] = [
		{
			id: 'cardNumber',
			header: t('common:card_number'),
			cell: (item) => (
				<span className='font-medium'>
					{item.cardNumber ?? 'DON!!'}
				</span>
			),
			sortKey: 'cardNumber',
			width: 180
		},
		{
			id: 'category',
			header: t('common:card_category'),
			cell: (item) => <Badge variant='secondary'>{item.category}</Badge>,
			sortKey: 'category',
			width: 160
		},
		{
			id: 'block',
			header: t('common:block'),
			cell: (item) => item.block ?? '—',
			sortKey: 'block',
			width: 130
		},
		{
			id: 'cost',
			header: t('common:cost'),
			cell: (item) => item.cost ?? '—',
			sortKey: 'cost',
			width: 110
		},
		{
			id: 'power',
			header: t('common:power'),
			cell: (item) => item.power ?? '—',
			sortKey: 'power',
			width: 120
		},
		{
			id: 'actions',
			header: t('common:actions'),
			cell: (item) => (
				<div className='flex justify-end gap-2'>
					<EditCardDialog card={item} />
					<ArchiveCatalogRecordButton id={item.id} type='card' />
				</div>
			),
			pinned: 'right',
			width: 220,
			minWidth: 220,
			maxWidth: 220,
			enableResizing: false
		}
	]

	function updateSearch(next: Partial<typeof search>) {
		navigate({ to: Route.fullPath, search: { ...search, ...next } })
	}

	return (
		<BackofficeShell contentClassName='max-w-none'>
			<header className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<p className='font-medium text-primary text-sm'>
						{t('common:catalog')}
					</p>
					<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
						{t('common:cards')}
					</h1>
				</div>
				<div>
					<CreateCardDialog />
				</div>
			</header>
			<div className='mt-6 flex flex-wrap gap-3'>
				<DebouncedSearchInput
					ariaLabel={t('common:search')}
					className='w-full sm:w-60'
					onValueChange={(q) =>
						updateSearch({ offset: 0, q: q || undefined })
					}
					placeholder={t('common:search')}
					value={search.q ?? ''}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_card_category')}
					items={[
						{
							label: t('common:all_card_categories'),
							value: 'all'
						},
						...CARD_CATEGORIES.map((value) => ({
							label: value,
							value
						}))
					]}
					label={t('common:card_category')}
					onValueChange={(value) =>
						updateSearch({
							category: CARD_CATEGORIES.find(
								(item) => item === value
							),
							offset: 0
						})
					}
					value={search.category ?? 'all'}
				/>
				<ResetFiltersButton
					disabled={!hasActiveFilters}
					onClick={() =>
						updateSearch({
							category: undefined,
							offset: 0,
							q: undefined
						})
					}
				/>
			</div>
			<DataTable
				className='mt-6 h-[calc(100vh-17rem)] min-h-96'
				columns={columns}
				data={data}
				pagination={{
					onPageChange: (pageIndex) =>
						updateSearch({ offset: pageIndex * limit }),
					onPageSizeChange: (nextLimit) =>
						updateSearch({ limit: nextLimit, offset: 0 }),
					pageCount: Math.ceil(
						(cardsQuery.data?.meta.total ?? 0) / limit
					),
					pageIndex: Math.floor(offset / limit),
					pageSize: limit
				}}
				sort={{
					onSortChange: (next) =>
						updateSearch({
							direction: next.direction,
							offset: 0,
							sortBy: next.by ?? 'cardNumber'
						}),
					state: { by: sortBy, direction }
				}}
				state={{
					empty: t('common:empty_cards'),
					error: cardsQuery.isError ? (
						<QueryErrorState
							message={t('common:catalog_load_error')}
							onRetry={() => cardsQuery.refetch()}
						/>
					) : (
						false
					),
					loading: cardsQuery.isPending
				}}
			/>
		</BackofficeShell>
	)
}
